// CTW-DURMAH-STUDENT-PAINPOINTS-SEO-RESOURCE-EXPANSION-02:
// Student journey cluster article: your first moot. The skills-level
// beginner guide the listing sites do not provide: how a moot actually
// runs, building a submission, handling judicial interventions, etiquette
// and nerves. Accuracy guardrails: conventions vary and each competition's
// own rules govern; Inn competitions are framed as typically requiring Inn
// membership at Bar-course stage; undergraduates start with internal
// university moots. Natural fit with Durmah's Speak Law positioning.

import type { ResourceSection, ResourceSourceGroup } from "./types";

export const yourFirstMootSections: ResourceSection[] = [
  {
    id: "what-a-moot-is",
    heading: "What a moot actually is",
    paragraphs: [
      "A moot is a mock appellate hearing argued on points of law. Two sides, usually two speakers each, appear before a judge and argue whether a fictional lower court got the law right. Nobody cross-examines witnesses and nobody disputes the facts; those belong to mock trials, which are a different exercise. In a moot the facts are fixed and the fight is entirely about legal argument.",
      "That is exactly why it is the best training a law degree offers. A moot takes the skills your essays and problem questions are quietly building, finding the issue, using authority, arguing application, and makes you do them out loud, under polite pressure, against someone arguing back. Most students are scared of their first moot. Most students are also better lawyers within a term of doing one.",
    ],
  },
  {
    id: "why-moot-early",
    heading: "Why it is worth doing early, whoever you want to become",
    paragraphs: [
      "Mooting is not only for future barristers. The abilities it trains, structured thinking on your feet, answering hard questions directly, staying composed while challenged, are exactly what solicitor interviews, assessment centres and seminar discussions reward. It is also one of the few CV lines a first-year can earn that genuinely demonstrates a legal skill rather than an interest in one.",
      "Guidance aimed at students consistently makes the same point: start early and start small. An internal novice moot in first year, with low stakes and a friendly judge, teaches more than watching a dozen finals. If the Bar is on your horizon, early mooting also feeds directly into scholarship and pupillage conversations later, where advocacy experience is expected material.",
    ],
    links: [
      {
        label: "Chambers Student: what is mooting?",
        href: "https://www.chambersstudent.co.uk/where-to-start/newsletter/what-is-mooting",
      },
    ],
  },
  {
    id: "how-a-moot-runs",
    heading: "How a moot typically runs",
    paragraphs: [
      "Formats vary between institutions and competitions, and the golden rule of mooting is that the competition's own rules govern everything: timings, documents, order of speeches, citation expectations. That said, a common shape will feel familiar wherever you moot:",
    ],
    bullets: [
      "A moot problem sets out the fictional case, the decision below, and the grounds of appeal.",
      "Each side usually fields a senior and a junior counsel, dividing the grounds between them.",
      "Written skeleton arguments are exchanged in advance, with strict page or word limits.",
      "Speeches run to fixed times, commonly with the appellant going first and a short right of reply.",
      "The judge interrupts with questions throughout. This is normal and intended.",
      "Results usually weigh advocacy quality heavily; you can lose the law and still win the moot.",
    ],
    links: [
      {
        label: "Oxford Faculty of Law: mooting, what it is and why take part",
        href: "https://www.law.ox.ac.uk/mooting-what-is-it-and-why-take-part/mooting-what-it-and-why-take-part",
      },
    ],
    callout: {
      title: "Read the rules twice",
      body: "Before you prepare anything, read your moot's rules from start to finish, then again the night before. More first moots go wrong on missed procedural details, timings, page limits, exchange deadlines, than on bad legal argument.",
    },
  },
  {
    id: "where-you-start",
    heading: "Where you actually start, and the eligibility reality",
    paragraphs: [
      "Your entry point is your own university: internal competitions, novice moots and law society events. That is where beginners are expected, judged kindly, and coached. University mooting programmes often run a ladder from speed moots up to international competitions, so there is a route upward without leaving home.",
      "Beyond that sit the national inter-university competitions, and beyond those the famous internationals. One eligibility point saves disappointment: many competitions run by the Inns of Court are open only to their own student members, typically at Bar-course stage rather than undergraduate level. Some Inns run schemes and societies that welcome earlier-stage aspiring barristers, but as an undergraduate your realistic ladder is internal moots first, then national inter-university competitions. Check each competition's own eligibility page before planning around it.",
    ],
    links: [
      {
        label: "LawCareers.Net: current mooting opportunities for students",
        href: "https://www.lawcareers.net/Explore/Features/21042026-Mooting-opportunities-for-students-202627",
      },
      {
        label: "ESU-Essex Court Chambers National Mooting Competition: official overview",
        href: "https://nationalmooting.org/about/",
      },
      {
        label: "University of Exeter Law School: a university mooting programme in practice",
        href: "https://law.exeter.ac.uk/study/mooting/",
      },
    ],
  },
  {
    id: "building-your-submission",
    heading: "Building your submission",
    paragraphs: [
      "Preparation starts the way a problem question does: read the moot problem twice, isolate exactly what your ground of appeal requires you to establish, and find the authorities that support each step. The skeleton argument then states your propositions in numbered, single-sentence form, each backed by its authority. If you have read our problem-question guide, the reasoning spine is the same one, performed aloud: issue, rule, application, conclusion, per ground.",
      "Draft your oral submission as a structure, not a script. A script collapses at the first judicial question; a structure survives anything. Know your opening sentence and your closing sentence word for word, know the order of your points in between, and practise delivering from the structure until it feels like explaining rather than reciting.",
    ],
  },
  {
    id: "knowing-your-authorities",
    heading: "Knowing your authorities properly",
    paragraphs: [
      "A moot judge's favourite move is to test whether you actually know the case you just cited. For every authority in your skeleton, you should be able to state the material facts in two sentences, the ratio in one, and why it helps you on these facts. If a case is central, read it in full; if the judge asks what happened in it and you cannot answer, the citation becomes a liability.",
      "Prepare your opponent's authorities with nearly the same care. Knowing in advance how you will distinguish their best case, different facts, narrower ratio, later doubted, is the difference between absorbing their argument and being flattened by it. Our case-reading guide's briefing method works unchanged here; the free official sources it lists are where you pull the full judgments.",
    ],
  },
  {
    id: "speaking-and-etiquette",
    heading: "Speaking, and the etiquette that steadies you",
    paragraphs: [
      "Court etiquette exists to make argument orderly, and once learned it is oddly calming: the forms give you something solid to stand on. Learn how your competition expects the judge to be addressed and use it consistently; the correct forms of address, for the bench and for your opponents, are stated in most moot rules and taught in university mooting guides. When in doubt, err on the side of formality and follow whatever your host's rules say.",
      "Delivery matters more than polish. Speak slower than feels natural, pause between points, and look at the judge rather than your notes. Refer to your opponents respectfully, never interrupt the judge, and when corrected, accept it gracefully and move on. Advocacy is persuasion, and persuasion reads as composed, courteous confidence rather than theatrical brilliance.",
    ],
    links: [
      {
        label: "University of Edinburgh LawPALS: archived 2013 mooting guide (PDF; check current competition rules)",
        href: "https://www.blogs.law.ed.ac.uk/lawpals/wp-content/uploads/sites/10/2013/10/A-Guide-to-Mooting-2013.pdf",
      },
    ],
  },
  {
    id: "judicial-interventions",
    heading: "Judicial interventions: the point, not the ambush",
    paragraphs: [
      "The judge's questions are the heart of a moot, and they are usually where it is won. A question is not an accusation; it is an invitation to show you can think rather than recite. The method: stop talking the moment the judge starts, listen to the whole question, pause a beat, then answer the question actually asked, directly, before steering back to your structure.",
      "Two habits mark out strong beginners. First, conceding well: if a point is genuinely lost, give it up gracefully and show why your argument survives without it; clinging to the indefensible costs far more. Second, honest uncertainty: if you do not know, say so and offer your best reasoned position rather than bluffing. Judges have seen every bluff. Calm reasoning under a question you did not expect is precisely the skill the whole exercise exists to build.",
    ],
  },
  {
    id: "nerves-and-first-moot-reality",
    heading: "Nerves, and what a first moot is really like",
    paragraphs: [
      "Almost nobody's first moot feels good from the inside. Voices shake, structures wobble, a question lands from nowhere and the room goes quiet. This is the universal experience, not a verdict on your potential, and judges of novice moots know exactly what they are watching. The realistic goal for a first moot is not to win; it is to stand up, get through your structure, answer some questions honestly and learn how the whole thing feels.",
      "Two preparations tame most of the fear. Watch a moot before you do one, your law school or mooting society will have recordings or open events, so the format holds no surprises. And practise aloud, several times, ideally with someone interrupting you with questions. Reading your notes silently is not preparation for a speaking exercise; saying the argument until your mouth knows it is.",
    ],
  },
  {
    id: "after-the-moot",
    heading: "After the moot, and where Durmah fits",
    paragraphs: [
      "The learning concentrates in the half hour after you sit down. Write down the judge's feedback while it is fresh, note the questions that hurt, and identify the one thing you will do differently next time. Students who moot twice a year with that habit improve startlingly fast; the skill compounds like everything else in this degree.",
      "Durmah's place is practice, not performance. Use it to pressure-test your understanding of the authorities you plan to cite, to rehearse explaining your argument in plain speech, and to check the terminology of the area your moot problem lives in. It will not write your skeleton or your speech, because the moot is assessing you, and the composure you are training only grows when the reasoning is your own. Speaking law out loud is a skill Durmah cares about deeply; the practice room is yours.",
    ],
  },
];

export const yourFirstMootSources: ResourceSourceGroup[] = [
  {
    topic: "Mooting guides and competitions",
    sources: [
      {
        label: "Chambers Student: what is mooting?",
        href: "https://www.chambersstudent.co.uk/where-to-start/newsletter/what-is-mooting",
      },
      {
        label: "LawCareers.Net: mooting opportunities for students",
        href: "https://www.lawcareers.net/Explore/Features/21042026-Mooting-opportunities-for-students-202627",
      },
      {
        label: "ESU-Essex Court Chambers National Mooting Competition: official overview",
        href: "https://nationalmooting.org/about/",
      },
    ],
  },
  {
    topic: "University and Inn mooting in practice",
    sources: [
      {
        label: "Oxford Faculty of Law: mooting, what it is and why take part",
        href: "https://www.law.ox.ac.uk/mooting-what-is-it-and-why-take-part/mooting-what-it-and-why-take-part",
      },
      {
        label: "University of Exeter Law School: mooting programme",
        href: "https://law.exeter.ac.uk/study/mooting/",
      },
      {
        label: "Inner Temple: student mooting society",
        href: "https://www.innertemple.org.uk/membership-services-support/societies/student-societies/mooting-society/",
      },
    ],
  },
];
